import React, {useCallback, useEffect, useState} from 'react';
import styled from 'styled-components';
import {Typography, VerticalSpacer} from 'epic-ue-ui';
import EducationFormFields from './EducationFormFields';
import {useEducation} from '../../jobs.hooks';
import {withLocalization} from '@epic-core/localization';

const Styled = styled.div`
    max-width: 100%;
`;

export interface EducationFormFieldsBlockProps {
    jobId: string;
    educationRequired?: boolean;
    pleaseSelectLabel?: string;
    isDisabled?: boolean;
    sectionRequiredLabel?: string;
    jobType?: string;
    form?: any;
    getMessage: (key: string) => string;
}

const EducationFormFieldsBlock = ({
    getMessage,
    jobId,
    educationRequired,
    pleaseSelectLabel,
    isDisabled = false,
    sectionRequiredLabel,
    form,
    jobType
}: EducationFormFieldsBlockProps): JSX.Element => {
    const {educations: schools} = useEducation(jobId, 'schools', 1);
    const {educations: degrees} = useEducation(jobId, 'degrees');
    const {educations: disciplines} = useEducation(jobId, 'disciplines');

    const [totalEducationBlocks, setTotalEducationBlocks] = useState(1);
    const [schoolsListPage, setSchoolsListPage] = useState(2);
    const [schoolsSearchTerm, setSchoolsSearchTerm] = useState('');
    const {educations: nextPageSchools, isLoading: nextPageSchoolsIsLoading} = useEducation(
        jobId,
        'schools',
        schoolsListPage
    );
    const {educations: searchSchools, isLoading: searchSchoolsIsLoading} = useEducation(
        jobId,
        'schools',
        1,
        schoolsSearchTerm
    );

    const [schoolsList, setSchoolsList] = useState(schools?.items || []);

    const [educationsBlocks, setEducationsBlocks] = useState<JSX.Element | null>(null);

    const handleSchoolScroll = useCallback(() => {
        setSchoolsListPage(schoolsListPage + 1);
    }, [schoolsListPage, setSchoolsListPage]);

    const handleSchoolSearch = useCallback(
        (newValue) => {
            setSchoolsSearchTerm(newValue);
        },
        [setSchoolsSearchTerm]
    );

    useEffect(() => {
        if (!searchSchoolsIsLoading) {
            setSchoolsList(searchSchools?.items || schoolsList);
        }
    }, [schoolsSearchTerm, searchSchools]);

    useEffect(() => {
        if (!nextPageSchoolsIsLoading) {
            let newSchools: {id: number; text: string}[] = [];
            if (schoolsList && nextPageSchools?.items) {
                newSchools = [...schoolsList, ...nextPageSchools.items];
            }
            setSchoolsList(newSchools);
        }
    }, [schoolsListPage, nextPageSchools, nextPageSchoolsIsLoading]);

    useEffect(() => {
        setSchoolsList(schools?.items || []);
    }, [schools]);

    const removeEducationBlock = (e) => {
        e.preventDefault();
        setTotalEducationBlocks(totalEducationBlocks - 1);
    };

    const appendEducationBlock = (e) => {
        e.preventDefault();
        setTotalEducationBlocks(totalEducationBlocks + 1);
    };

    useEffect(() => {
        setEducationsBlocks(
            <>
                {[...Array(totalEducationBlocks)].map((e, i) => {
                    return (
                        <EducationFormFields
                            key={`education-form-field-${i}`}
                            educationBlockIndex={i}
                            schools={schoolsList || []}
                            degrees={degrees?.items || []}
                            disciplines={disciplines?.items || []}
                            educationRequired={educationRequired}
                            pleaseSelectLabel={pleaseSelectLabel}
                            handleSchoolScroll={handleSchoolScroll}
                            handleSchoolSearch={handleSchoolSearch}
                            removeEducationBlock={removeEducationBlock}
                            isDisabled={isDisabled}
                            sectionRequiredLabel={sectionRequiredLabel}
                            form={form}
                            jobType={jobType}
                        />
                    );
                })}
            </>
        );
    }, [schoolsList, degrees, disciplines, totalEducationBlocks, isDisabled]);

    return (
        <Styled className="education-section">
            {educationsBlocks && (
                <>
                    <VerticalSpacer variant="eyebrow" />
                    <Typography variant="h3">Education</Typography>
                    <VerticalSpacer variant="heading" />
                    {educationsBlocks}
                    <VerticalSpacer variant="eyebrow" />
                    <a
                        role="button"
                        tabIndex={0}
                        onClick={appendEducationBlock}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                appendEducationBlock(e);
                            }
                        }}>
                        {getMessage('epic.jobs.form.education.add')}
                    </a>
                    <VerticalSpacer variant="heading" />
                </>
            )}
        </Styled>
    );
};

export default withLocalization(EducationFormFieldsBlock);
